import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Package, MapPin, BookMarked } from "lucide-react";
import { branchesApi, type Branch, type BranchInventoryItem } from "@/lib/api/branches";

const formatAccessionNumber = (n: number) => {
  if (n == null) return null;
  const s = String(n);
  return s.padStart(6, "0");
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "AVAILABLE":
      return "bg-green-100 text-green-800 border-green-200";
    case "CHECKED_OUT":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "ON_HOLD":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "TRANSFERRING":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "MISSING":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const BranchInventoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [items, setItems] = useState<BranchInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [branchData, itemsData] = await Promise.all([
        branchesApi.getById(id!),
        branchesApi.getItems(id!),
      ]);
      setBranch(branchData);
      setItems(itemsData);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load branch inventory.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="wide">
        <PageHeader
          title="Branch Inventory"
          description={`${items.length} item${items.length !== 1 ? "s" : ""} at ${branch?.name ?? "this branch"}`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch?.name ?? "Branch", href: id ? `/setup/branches/${id}` : undefined },
            { label: "Inventory" },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          actions={
            <Button onClick={() => navigate("/catalog")} variant="outline" className="gap-2">
              Browse Catalog
              <ArrowRight className="w-4 h-4" />
            </Button>
          }
        />

        {items.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              No items at this branch yet
            </h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Items appear here when they are assigned to this branch during cataloging, or when a
              transfer to this branch is completed.
            </p>
            <Button onClick={() => navigate("/catalog")} variant="outline" className="mt-6 gap-2">
              Go to Catalog
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">
                        Barcode
                      </th>
                      <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">
                        Title / Author
                      </th>
                      <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">
                        Copy
                      </th>
                      <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">
                        Type / Location
                      </th>
                      <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">
                        Status
                      </th>
                      <th className="text-right p-3 text-xs font-medium text-gray-600 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <span className="font-mono text-sm">{item.barcode}</span>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-gray-900 line-clamp-1">
                              {item.biblio?.title || "—"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.biblio?.authors?.join(", ") || "—"}
                            </p>
                          </div>
                        </td>
                        <td className="p-3 text-sm">
                          #{item.copyNumber}{" "}
                          <span className="text-gray-400">
                            (Acc: {formatAccessionNumber(item.accessionNumber)})
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          <div>
                            <span>{item.itemType?.name || "—"}</span>
                            {item.location?.name && (
                              <span className="text-gray-500">
                                {" / "}
                                <MapPin className="inline w-3 h-3" />
                                {item.location.name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={`text-xs border ${getStatusColor(item.statusCode)}`}>
                            {item.statusCode?.replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1"
                            onClick={() =>
                              item.biblio?.id && navigate(`/catalog/physical/${item.biblio.id}`)
                            }
                            disabled={!item.biblio?.id}
                          >
                            <BookMarked className="w-4 h-4" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default BranchInventoryPage;
